import { MESSAGE_TYPE_GROUPS } from "features/results/constants/messageTypeGroups";
import { useStaffCommonResults } from "features/results/hooks/useStaffCommonResults";

// Every staff results tab now runs through the common results endpoint
// (`/healthlink/common/results`). Laboratory and Radiology are narrowed by the
// matching `messageTypes` group instead of hitting their dedicated endpoints —
// the common endpoint paginates server-side, the dedicated ones don't. Their
// tabs keep their own columns/detail views via the isLaboratory/isRadiology flags.
/*
 * One empty array, not a fresh one per render. `data?.results ?? []` handed out a new array
 * every time the query had no data yet, so anything watching `results` by identity saw a change
 * on every render — which put the results page into an infinite render loop ("Maximum update
 * depth exceeded") the moment one did (KIN-2089's onResultsLoaded).
 *
 * Frozen so the shared instance cannot be mutated by a caller into everyone else's problem.
 */
const NO_RESULTS = Object.freeze([]);

export const useStaffResultsData = ({
  type,
  orderBy,
  messageTypes,
  searchQuery,
  unassigned,
  unreviewed,
  limit,
  offset,
  enabled = true,
}) => {
  const isLaboratory = type === "laboratory";
  const isRadiology = type === "radiology";
  const isCommon = type === "common";

  const effectiveMessageTypes = isLaboratory
    ? [MESSAGE_TYPE_GROUPS.laboratory]
    : isRadiology
    ? [MESSAGE_TYPE_GROUPS.radiology]
    : messageTypes;

  const { data, isLoading, isError, error } = useStaffCommonResults({
    orderBy,
    messageTypes: effectiveMessageTypes,
    searchQuery,
    unassigned,
    unreviewed,
    limit,
    offset,
    enabled,
  });

  return {
    results: data?.results ?? NO_RESULTS,
    totalCount: data?.totalCount ?? 0,
    currentOffset: data?.currentOffset ?? 0,
    serverPaginated: data?.serverPaginated ?? false,
    isLoading,
    isError,
    error,
    isLaboratory,
    isRadiology,
    isCommon,
  };
};
